import React, { useState, useEffect } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { SortablePlaylistItemProps } from "../../../../types/screensTypes";
import { Playlist } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import { getPlayList, savePlayList } from "../../../../api";
import AudioPlayerRewind from "../../../../components/common/AudioPlayer";
import { FileInput } from "../../../../components/common/FileInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../api/s3";
import downloadIcon from "../../../../assets/images/download.png";
import s from "../../../../components/common/ContentSurface.module.css";
import audioFooter from "../../../../components/common/AudioPlayerFooter.module.css";
import styles from "./Music.module.css";
import stepStyles from "../createStep.module.css";
import Icon from "../../../../components/common/Icon";

const getPlaylistFilename = (voice: Playlist): string => {
    try {
        const pathname = new URL(voice.url).pathname;
        const basename = pathname.split("/").pop();
        if (basename && basename.includes(".")) return basename;
    } catch {
        // fall through
    }
    return `playlist-${voice.position}.mp3`;
};

const downloadPlaylistItem = async (voice: Playlist): Promise<void> => {
    const response = await fetch(voice.url, { cache: "reload" });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", getPlaylistFilename(voice));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const SortableItem: React.FC<SortablePlaylistItemProps> = ({
    id,
    voice,
    onPlay,
    onRemove,
    onDownload,
    isSelected,
    onToggleSelected,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} className={styles.voiceItem}>
            <div className={styles.voiceLeft}>
                <input
                    type="checkbox"
                    className={s.checkbox}
                    checked={isSelected}
                    onChange={() => onToggleSelected(id)}
                    onClick={(e) => e.stopPropagation()}
                    title={t("select")}
                />
                <div className={styles.dragHandle} {...listeners} title={t("dragToReorder") || ""}>
                    <svg width="18" height="18" viewBox="0 0 15 15" fill="currentColor" aria-hidden>
                        <path d="M5 2.5H10M5 5.5H10M5 8.5H10M5 11.5H10" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                </div>
                <button
                    type="button"
                    className={styles.playBtn}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onPlay(voice);
                    }}
                    aria-label={t("play")}
                >
                    <Icon name="ic-play" />
                </button>
                <div className={styles.voiceInfo}>
                    <h4>
                        {t("playListItem")}
                        {voice.position}
                    </h4>
                </div>
            </div>
            <div className={styles.actions}>
                <button
                    type="button"
                    className={styles.downloadBtn}
                    onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDownload(voice);
                    }}
                    aria-label={t("download")}
                >
                    <img src={downloadIcon} alt="" />
                </button>
                <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove(voice.id);
                    }}
                >
                    {t("remove")}
                </button>
            </div>
        </div>
    );
};

interface TrackData {
    trackId: string;
    type?: string;
}

const Music: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { trackData } = useTrackCreation() as { trackData: TrackData };
    const [music, setMusic] = useState<Playlist[]>([]);
    const [selectedVoice, setSelectedVoice] = useState<Playlist | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [downloading, setDownloading] = useState<boolean>(false);

    const getPlayListAPI = async (trackId: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !trackId) return;
        const music = await getPlayList({ token, trackId });

        const sortedMusic: Playlist[] = music.sort((a: Playlist, b: Playlist) => a.position - b.position);
        setMusic(sortedMusic);
        setSelectedIds(new Set());
    };

    const saveHohoMusicAPI = async (): Promise<void> => {
        setLoading(true);
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;

        try {
            await savePlayList({ token, trackId, music });
            await getPlayListAPI(trackData.trackId);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error saving playlist:", error);
        }
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = music.findIndex((item) => item.id === active.id);
            const newIndex = music.findIndex((item) => item.id === over.id);
            const reorderedMusic = arrayMove(music, oldIndex, newIndex).map((item, index) => ({
                ...item,
                position: index + 1,
            }));

            setMusic(reorderedMusic);
        }
    };

    const handleFileUpload = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    const updatedMusic = music
                        .concat({
                            id: Date.now().toString(),
                            url: result,
                            position: music.length + 1,
                            trackId: trackData.trackId,
                            new: true,
                        })
                        .map((item, index) => ({
                            ...item,
                            position: index + 1,
                        }));
                    setMusic(updatedMusic);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const removeVoice = (id: string | number): void => {
        const updatedMusic = music
            .filter((voice) => voice.id !== id)
            .map((item, index) => ({
                ...item,
                position: index + 1,
            }));
        setMusic(updatedMusic);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(String(id));
            return next;
        });
    };

    const toggleSelected = (id: string): void => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const allSelected = music.length > 0 && music.every((item) => selectedIds.has(item.id));

    const toggleSelectAll = (): void => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(music.map((item) => item.id)));
        }
    };

    const removeSelected = (): void => {
        if (selectedIds.size === 0) return;
        const updatedMusic = music
            .filter((voice) => !selectedIds.has(voice.id))
            .map((item, index) => ({
                ...item,
                position: index + 1,
            }));
        setMusic(updatedMusic);
        setSelectedIds(new Set());
        if (selectedVoice && selectedIds.has(selectedVoice.id)) {
            setSelectedVoice(null);
        }
    };

    const handleDownload = async (voice: Playlist): Promise<void> => {
        try {
            await downloadPlaylistItem(voice);
        } catch (error) {
            console.error("Error downloading audio:", error);
        }
    };

    const downloadSelected = async (): Promise<void> => {
        if (selectedIds.size === 0 || downloading) return;
        setDownloading(true);
        const items = music.filter((item) => selectedIds.has(item.id));
        try {
            for (let i = 0; i < items.length; i++) {
                await downloadPlaylistItem(items[i]);
                if (i < items.length - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 400));
                }
            }
        } catch (error) {
            console.error("Error downloading selected audio:", error);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getPlayListAPI(trackData.trackId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    );

    return (
        <div className={`step-container ${styles.root}`}>
            <TitleSubtitle
                title={t("playlist")}
                subtitle={t("addMusicInBetweenStoriesDuringYourTour")}
                layout="music"
            />

            <hr className={styles.rule} />

            <div className={styles.upload}>
                <h4 className={styles.uploadHeading}>{t("uploadAudio")}</h4>
                <FileInput
                    onFileSelected={handleFileUpload}
                    showButton={true}
                    placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                    buttonText={t("uploadAudio")}
                />
            </div>

            {music.length > 0 && (
                <div className={s.selectAllRow}>
                    <label className={s.selectAllLabel}>
                        <input
                            type="checkbox"
                            className={s.checkbox}
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        {t("selectAll")}
                    </label>
                    {selectedIds.size > 0 && (
                        <div className={s.selectActions}>
                            <button
                                type="button"
                                className={s.ttsBtn}
                                disabled={downloading}
                                onClick={downloadSelected}
                            >
                                {t("download")} ({selectedIds.size})
                            </button>
                            <button type="button" className={s.bulkDeleteBtn} onClick={removeSelected}>
                                {t("deleteSelected")} ({selectedIds.size})
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className={styles.listWrap}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={music.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                        <div className={styles.list}>
                            {music.map((voice) => (
                                <SortableItem
                                    key={voice.id}
                                    id={voice.id}
                                    voice={voice}
                                    onPlay={setSelectedVoice}
                                    onRemove={removeVoice}
                                    onDownload={handleDownload}
                                    isSelected={selectedIds.has(voice.id)}
                                    onToggleSelected={toggleSelected}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    onClick={saveHohoMusicAPI}
                    label={loading ? t("saving") : t("save")}
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${styles.saveBtn}`}
                />
            </div>

            {selectedVoice && (
                <div className={audioFooter.section}>
                    <AudioPlayerRewind
                        title={t("playListItem") + selectedVoice.position}
                        src={selectedVoice.url}
                        isAutoPlay={true}
                    />
                </div>
            )}
        </div>
    );
};

export default Music;
